import React, { useState } from 'react';

import { Segmented } from 'antd';

import NwDrawer from '@components/Gui/NwDrawer';
import AdvPaymentForm from '@components/Transactions/TransactionsCreateForms/AdvPaymentForm';
import CommAsMothAgForm from '@components/Transactions/TransactionsCreateForms/CommAsMothAgForm';
import CreditForm from '@components/Transactions/TransactionsCreateForms/CreditForm';
import DebitForm from '@components/Transactions/TransactionsCreateForms/DebitForm';
import ExpenseFormContact from '@components/Transactions/TransactionsCreateForms/ExpenseFormContact';
import MothAgCommAdjForm from '@components/Transactions/TransactionsCreateForms/MothAgCommAdjForm';
import MothAgCommForm from '@components/Transactions/TransactionsCreateForms/MothAgCommForm';

import { TransactionDrawerContent } from '../Project/ProjectTransactions/Transactions/ProjectTransactionsUI';
import { useStatementContext, useStatementDispatch } from './StatementContext';

/**
 * AddTransactionDrawer (Universal)
 *
 * Drawer for creating a transaction for any contact type using the unified
 * ContactsStatement context. It mirrors the behavior of AddContactTransactionDrawer
 * and adapts agency passive/active logic to the new rules:
 *   passive when (contactType === 'agency' && statementType === 'client'), otherwise active
 */
const AddTransactionDrawer = ({ onClose, contactType: overrideContactType, contactID: overrideContactID }) => {
    const drawerWidth = '50vw';
    const drawerMinWidth = '980px';
    const [selectedTab, setSelectedTab] = useState('Expense');
    const [touchForm, setTouchForm] = useState(false);

    const { statementType, contactID, contactType } = useStatementContext();
    const csDispatch = useStatementDispatch();

    const effectiveContactType = overrideContactType ?? contactType;
    const effectiveContactID = overrideContactID ?? contactID;

    const afterTransactionCreated = () => {
        csDispatch({ type: 'StartReload' });
        onClose();
    };

    const getFamilyOptions = () => {
        const options = [];

        // Model specific
        if (statementType === 'model') {
            options.push({
                label: (
                    <div>
                        <h2>Expense</h2>
                        <small></small>
                    </div>
                ),
                value: 'Expense',
            });
            options.push({
                label: (
                    <div>
                        <h2>Adv. Payment</h2>
                        <small></small>
                    </div>
                ),
                value: 'AdvPayment',
            });
        }

        // Agency-specific commission options follow the new rules, only when contact is an agency
        if (effectiveContactType === 'agency') {
            if (statementType === 'agency') {
                options.push({
                    label: (
                        <div>
                            <h2>Commission (+)</h2>
                            <small>mother agency commission</small>
                        </div>
                    ),
                    value: 'MothAgComm',
                });
                options.push({
                    label: (
                        <div>
                            <h2>Commission Adj (-)</h2>
                            <small>mother agency commission adjustment</small>
                        </div>
                    ),
                    value: 'MothAgCommAdj',
                });
            } else if (statementType === 'client') {
                options.push({
                    label: (
                        <div>
                            <h2>Commission (-)</h2>
                            <small>commission as mother agency</small>
                        </div>
                    ),
                    value: 'CommAsMothAg',
                });
            }
        }

        // Expense for non-model statements too
        if (statementType !== 'model') {
            options.push({
                label: (
                    <div>
                        <h2>Expense</h2>
                        <small></small>
                    </div>
                ),
                value: 'Expense',
            });
        }

        // Always available
        options.push({
            label: (
                <div>
                    <h2>Debit</h2>
                    <small></small>
                </div>
            ),
            value: 'Debit',
        });
        options.push({
            label: (
                <div>
                    <h2>Credit</h2>
                    <small></small>
                </div>
            ),
            value: 'Credit',
        });

        return options;
    };

    // Map statementType to form API props
    let statementTypeForms = '';
    switch (statementType) {
        case 'client':
            statementTypeForms = 'Client';
            break;
        case 'agency':
            statementTypeForms = 'Agency';
            break;
        case 'service':
            statementTypeForms = 'Service';
            break;
        default:
            statementTypeForms = 'Model';
            break;
    }

    // No need to compute or pass agencyStatementType; forms will rely on statementType only

    const FamilyCreateForm = () => {
        return (
            <TransactionDrawerContent>
                <Segmented block value={selectedTab} onChange={setSelectedTab} options={getFamilyOptions()} />
                {selectedTab === 'Expense' && (
                    <ExpenseFormContact
                        contactID={effectiveContactID}
                        statementType={statementTypeForms}
                        onCloseDrawer={onClose}
                        onTransactionCreated={afterTransactionCreated}
                        width={drawerWidth}
                        minWidth={drawerMinWidth}
                    />
                )}
                {selectedTab === 'AdvPayment' && (
                    <AdvPaymentForm
                        modelID={effectiveContactID}
                        onCloseDrawer={onClose}
                        onTransactionCreated={afterTransactionCreated}
                        width={drawerWidth}
                        minWidth={drawerMinWidth}
                    />
                )}
                {selectedTab === 'CommAsMothAg' && (
                    <CommAsMothAgForm
                        agencyID={effectiveContactID}
                        onCloseDrawer={onClose}
                        onTransactionCreated={afterTransactionCreated}
                        width={drawerWidth}
                        minWidth={drawerMinWidth}
                    />
                )}
                {selectedTab === 'MothAgComm' && (
                    <MothAgCommForm
                        agencyID={effectiveContactID}
                        onCloseDrawer={onClose}
                        onTransactionCreated={afterTransactionCreated}
                        width={drawerWidth}
                        minWidth={drawerMinWidth}
                    />
                )}
                {selectedTab === 'MothAgCommAdj' && (
                    <MothAgCommAdjForm
                        agencyID={effectiveContactID}
                        onCloseDrawer={onClose}
                        onTransactionCreated={afterTransactionCreated}
                        width={drawerWidth}
                        minWidth={drawerMinWidth}
                    />
                )}
                {selectedTab === 'Debit' && (
                    <DebitForm
                        contactID={effectiveContactID}
                        statementType={statementTypeForms}
                        onCloseDrawer={onClose}
                        onTransactionCreated={afterTransactionCreated}
                        width={drawerWidth}
                        minWidth={drawerMinWidth}
                    />
                )}
                {selectedTab === 'Credit' && (
                    <CreditForm
                        contactID={effectiveContactID}
                        statementType={statementTypeForms}
                        onCloseDrawer={onClose}
                        onTransactionCreated={afterTransactionCreated}
                        width={drawerWidth}
                        minWidth={drawerMinWidth}
                    />
                )}
            </TransactionDrawerContent>
        );
    };

    return (
        <NwDrawer
            title="Add Transaction"
            onClose={onClose}
            visible={true}
            width={drawerWidth}
            minWidth={drawerMinWidth}
            touchForm={touchForm}
            onTouchForm={setTouchForm}
        >
            <FamilyCreateForm />
        </NwDrawer>
    );
};

export default AddTransactionDrawer;
